import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Collapse, Empty, Flex, Row, Statistic, Steps, Table, Typography } from 'antd';
import { EditOutlined, LineChartOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import LspivService from '../../../services/LspivService';
import { clrs } from '../../../constants/colors';
import FrameCanvas, { Line } from './FrameCanvas';

const { Title, Text } = Typography;

const STAGES = ['calibration', 'mask', 'piv', 'discharge'];

const fmt = (v, digits) => (v === null || v === undefined ? '—' : Number(v).toFixed(digits));

const ResultStep = ({ jobId, job, onBack }) => {
    const { t } = useTranslation();
    const [result, setResult] = useState(null);

    const status = job?.status || {};
    const running = status.status === 'running' && status.step === 'analysis';
    const failed = status.status === 'error' && status.step === 'analysis';
    const done = status.step === 'done';

    useEffect(() => {
        if (!done) { setResult(null); return; }
        LspivService.getResult(jobId).then(setResult).catch(() => setResult(null));
    }, [jobId, done, status.updated_at]);

    if (running) {
        const current = Math.max(0, STAGES.indexOf(status.stage));
        return (
            <Flex vertical gap={24} style={{ padding: '16px 0' }}>
                <Title level={4} style={{ margin: 0 }}>{t('lspiv.result.runningTitle')}</Title>
                <Steps
                    direction="vertical"
                    size="small"
                    current={current}
                    items={STAGES.map((s, i) => ({
                        title: t(`lspiv.result.stage.${s}`),
                        icon: i === current ? <LoadingOutlined /> : undefined,
                    }))}
                />
                <Text type="secondary">{t('lspiv.result.runningHint')}</Text>
            </Flex>
        );
    }

    if (failed) {
        return (
            <Flex vertical gap={16}>
                <Alert type="error" showIcon message={t('lspiv.result.failed')} description={status.error} />
                <div><Button icon={<EditOutlined />} onClick={onBack}>{t('lspiv.result.editMarkup')}</Button></div>
            </Flex>
        );
    }

    if (!result) {
        return <Empty description={t('lspiv.result.empty')} />;
    }

    const stations = result.stations || [];
    const sec = result.section_px;
    const sectionLen = Math.hypot(sec.xr - sec.xl, sec.yr - sec.yl);
    const maxShift = Math.max(...stations.map((s) => Math.hypot(s.dx || 0, s.dy || 0)), 1e-9);
    // Стрелки масштабируем для наглядности: самая длинная — пятая часть створа.
    const arrowScale = sectionLen / 5 / maxShift;

    return (
        <Flex vertical gap={24}>
            <Title level={4} style={{ margin: 0 }}>
                <LineChartOutlined /> {t('lspiv.result.title')}
            </Title>

            <Card style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
                <Row gutter={[32, 16]}>
                    <Col xs={24} md={8}>
                        <Statistic
                            title={t('lspiv.result.discharge')}
                            value={fmt(result.discharge, 3)}
                            suffix={t('lspiv.result.unitQ')}
                            valueStyle={{ color: clrs.blue, fontWeight: 700, fontSize: '2.2rem' }}
                        />
                        {result.discharge_std !== null && (
                            <Text type="secondary">± {fmt(Math.abs(result.discharge_std), 3)} {t('lspiv.result.unitQ')}</Text>
                        )}
                    </Col>
                    <Col xs={12} md={4}>
                        <Statistic title={t('lspiv.result.meanVelocity')} value={fmt(result.mean_velocity, 2)} suffix={t('lspiv.result.unitV')} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Statistic title={t('lspiv.result.surfaceVelocity')} value={fmt(result.surface_velocity, 2)} suffix={t('lspiv.result.unitV')} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Statistic title={t('lspiv.result.width')} value={fmt(result.width, 2)} suffix={t('lspiv.result.unitM')} />
                    </Col>
                    <Col xs={12} md={4}>
                        <Statistic title={t('lspiv.result.area')} value={fmt(result.area, 2)} suffix={t('lspiv.result.unitA')} />
                    </Col>
                </Row>
            </Card>

            <div>
                <FrameCanvas src={LspivService.frameUrl(jobId, result.frame)}>
                    {({ unit }) => (
                        <>
                            <defs>
                                <marker id="lspiv-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="4" markerHeight="4" orient="auto-start-reverse">
                                    <path d="M 0 0 L 10 5 L 0 10 z" fill="#ADFF00" />
                                </marker>
                            </defs>
                            <Line a={[sec.xl, sec.yl]} b={[sec.xr, sec.yr]} unit={unit} color="#1677FF" />
                            {stations.filter((s) => s.velocity > 0).map((s, i) => (
                                <line
                                    key={i}
                                    x1={s.x}
                                    y1={s.y}
                                    x2={s.x + (s.dx || 0) * arrowScale}
                                    y2={s.y + (s.dy || 0) * arrowScale}
                                    stroke="#ADFF00"
                                    strokeWidth={unit * 0.45}
                                    markerEnd="url(#lspiv-arrow)"
                                />
                            ))}
                        </>
                    )}
                </FrameCanvas>
                <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>{t('lspiv.result.arrowsHint')}</Text>
            </div>

            <Collapse
                size="small"
                items={[{
                    key: 'table',
                    label: t('lspiv.result.details'),
                    children: (
                        <Flex vertical gap={12}>
                            <Text type="secondary">
                                {t('lspiv.result.params', {
                                    depth: fmt(result.average_depth, 2),
                                    maxDepth: fmt(result.max_depth, 2),
                                    alpha: result.alpha,
                                    frames: result.frames,
                                })}
                            </Text>
                            <Table
                                size="small"
                                pagination={false}
                                rowKey="distance"
                                dataSource={stations}
                                columns={[
                                    { title: t('lspiv.result.colDistance'), dataIndex: 'distance', render: (v) => fmt(v, 2) },
                                    { title: t('lspiv.result.colDepth'), dataIndex: 'depth', render: (v) => fmt(v, 2) },
                                    { title: t('lspiv.result.colVelocity'), dataIndex: 'velocity', render: (v) => fmt(v, 3) },
                                    { title: t('lspiv.result.colQ'), dataIndex: 'q', render: (v) => fmt(v, 4) },
                                ]}
                            />
                        </Flex>
                    ),
                }]}
            />

            <div><Button icon={<EditOutlined />} onClick={onBack}>{t('lspiv.result.editMarkup')}</Button></div>
        </Flex>
    );
};

export default ResultStep;
